import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Table, Badge, Alert, Modal, Spinner, Toast } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import attendanceService from '../services/attendanceService';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';

const AttendanceOverride = ({ sessionId, cohortId, onOverrideComplete }) => {
  const { currentUser } = useAuth();
  const [learners, setLearners] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [sessionType, setSessionType] = useState('teams');
  const [skipTeamsRequirement, setSkipTeamsRequirement] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [sessionInfo, setSessionInfo] = useState(null);
  const [lastOverrideAt, setLastOverrideAt] = useState(null);
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    if (sessionId) {
      loadSessionLearners();
    }
  }, [sessionId, cohortId]);

  const loadSessionLearners = async () => {
    setLoading(true);
    setError('');
    try {
      const sessionDoc = await getDoc(doc(db, 'sessions', sessionId));
      if (!sessionDoc.exists()) {
        setError('Session not found.');
        setLearners([]);
        return;
      }

      const sessionData = sessionDoc.data();
      setSessionInfo(sessionData);
      const effectiveCohortId = sessionData.cohortId || cohortId || null;

      let learnerList = [];
      if (effectiveCohortId) {
        const learnersQuery = query(
          collection(db, 'users'),
          where('role', '==', 'learner'),
          where('cohortId', '==', effectiveCohortId)
        );
        const learnersSnapshot = await getDocs(learnersQuery);
        learnerList = learnersSnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
      } else {
        const assignedEmails = Array.isArray(sessionData.assignedUserEmails)
          ? sessionData.assignedUserEmails.map((email) => String(email || '').toLowerCase().trim())
          : [];
        if (assignedEmails.length === 0) {
          setError('Session does not have a cohort or assigned learner emails.');
          setLearners([]);
          return;
        }
        for (let i = 0; i < assignedEmails.length; i += 10) {
          const chunk = assignedEmails.slice(i, i + 10);
          const learnersQuery = query(
            collection(db, 'users'),
            where('email', 'in', chunk)
          );
          const learnersSnapshot = await getDocs(learnersQuery);
          learnerList.push(...learnersSnapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data()
          })));
        }
      }

      const attendanceResult = await attendanceService.getSessionAttendance(sessionId);
      const attendanceMap = {};
      if (attendanceResult.success) {
        attendanceResult.attendance.forEach((record) => {
          attendanceMap[record.learnerId] = record.status || 'incomplete';
        });
      }

      const combined = learnerList.map((learner) => ({
        id: learner.id,
        name: learner.displayName || learner.name || learner.email || 'N/A',
        email: learner.email || 'N/A',
        currentStatus: attendanceMap[learner.id] || 'incomplete'
      }));
      setLearners(combined);
    } catch (err) {
      devError('AttendanceOverride: failed to load learners', err);
      setError('Failed to load learners');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (learnerId, status) => {
    setOverrides(prev => ({
      ...prev,
      [learnerId]: {
        ...prev[learnerId],
        status,
        reason: prev[learnerId]?.reason || ''
      }
    }));
  };

  const handleReasonChange = (learnerId, reason) => {
    setOverrides(prev => ({
      ...prev,
      [learnerId]: {
        ...prev[learnerId],
        reason,
        status: prev[learnerId]?.status || 'present'
      }
    }));
  };

  const handleSubmitOverrides = async () => {
    setLoading(true);
    setError('');

    try {
      const overrideData = Object.entries(overrides).map(([learnerId, override]) => ({
        learnerId,
        status: override.status,
        reason: override.reason,
        source: 'manual-override',
        sessionType,
        skipTeamsRequirement,
        notes,
        overriddenBy: currentUser.uid,
        overriddenAt: new Date().toISOString(),
        cohortId: sessionInfo?.cohortId || cohortId || null,
        moduleId: sessionInfo?.moduleId || null,
        date: sessionInfo?.scheduledDate || sessionInfo?.scheduledDateTime || null
      }));

      const result = await attendanceService.markAttendance(sessionId, overrideData, currentUser.uid);

      if (result.success) {
        setSuccess(`Successfully updated attendance for ${overrideData.length} learners`);
        setShowConfirmModal(false);
        setOverrides({});
        setNotes('');
        setLastOverrideAt(new Date());
        setShowToast(true);
        await loadSessionLearners();
        if (onOverrideComplete) {
          onOverrideComplete(result.attendanceRecords);
        }
      } else {
        setError(result.error || 'Failed to submit attendance overrides.');
      }
    } catch (err) {
      devError('AttendanceOverride: failed to submit overrides', err);
      setError(err?.message || 'Failed to submit attendance overrides');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      present: 'success',
      absent: 'danger',
      excused: 'warning',
      incomplete: 'info',
      self_paced: 'primary',
      other: 'secondary'
    };
    const labelMap = {
      self_paced: 'self-paced'
    };
    return <Badge bg={variants[status] || 'secondary'}>{labelMap[status] || status}</Badge>;
  };

  const getOverrideCount = () => {
    return Object.keys(overrides).length;
  };

  return (
    <Card>
      <Card.Header>
        <div className="d-flex align-items-center justify-content-between">
          <h5 className="mb-0">Manual Attendance Override</h5>
          {lastOverrideAt && (
            <Badge bg="success">Overrides saved</Badge>
          )}
        </div>
        <p className="text-muted mb-0">
          Override Teams attendance for in-person sessions, technical issues, or excused absences
        </p>
      </Card.Header>
      <Card.Body>
        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1050 }}>
          <Toast show={showToast} onClose={() => setShowToast(false)} bg="success">
            <Toast.Header closeButton={false}>
              <strong className="me-auto">✅ Overrides Saved</strong>
            </Toast.Header>
            <Toast.Body className="text-white">
              Attendance overrides saved successfully.
            </Toast.Body>
          </Toast>
        </div>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        {lastOverrideAt && (
          <Alert variant="info">
            Last override saved at {lastOverrideAt.toLocaleString('en-ZA')}.
          </Alert>
        )}

        {/* Session Configuration */}
        <div className="mb-4">
          <h6>Session Configuration</h6>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Session Type</Form.Label>
                <Form.Select
                  value={sessionType}
                  onChange={(e) => setSessionType(e.target.value)}
                >
                  <option value="teams">Teams Meeting</option>
                  <option value="in-person">In-Person Session</option>
                  <option value="hybrid">Hybrid Session</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="skip-teams-switch"
                  label="Skip Teams Attendance Requirement"
                  checked={skipTeamsRequirement}
                  onChange={(e) => setSkipTeamsRequirement(e.target.checked)}
                />
                <Form.Text className="text-muted">
                  Use for in-person sessions or when Teams data is unavailable
                </Form.Text>
              </Form.Group>
            </div>
          </div>
        </div>

        {/* Session Notes */}
        <Form.Group className="mb-4">
          <Form.Label>Session Notes</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any general notes about this session..."
          />
        </Form.Group>

        {/* Learner Attendance Table */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6>Learner Attendance ({learners.length} learners)</h6>
            {getOverrideCount() > 0 && (
              <Badge bg="info">{getOverrideCount()} overrides pending</Badge>
            )}
          </div>

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading learners...</p>
            </div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Learner</th>
                  <th>Email</th>
                  <th>Current Status</th>
                  <th>Override Status</th>
                  <th>Reason/Notes</th>
                </tr>
              </thead>
              <tbody>
                {learners.map(learner => (
                  <tr key={learner.id}>
                    <td>
                      <strong>{learner.name}</strong>
                    </td>
                    <td>
                      <small>{learner.email}</small>
                    </td>
                    <td>
                      {getStatusBadge(learner.currentStatus)}
                    </td>
                    <td>
                      <Form.Select
                        size="sm"
                        value={overrides[learner.id]?.status || ''}
                        onChange={(e) => handleStatusChange(learner.id, e.target.value)}
                      >
                        <option value="">No override</option>
                        <option value="present">Present (P)</option>
                        <option value="absent">Absent (A)</option>
                        <option value="excused">Excused (E)</option>
                        <option value="self_paced">Self-paced (SP)</option>
                        <option value="other">Other (O)</option>
                        <option value="incomplete">Incomplete</option>
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="text"
                        placeholder="Reason for override..."
                        value={overrides[learner.id]?.reason || ''}
                        onChange={(e) => handleReasonChange(learner.id, e.target.value)}
                        disabled={!overrides[learner.id]?.status}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>

        {/* Submit Button */}
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <small className="text-muted">
              <strong>Legend:</strong>
              <span className="badge bg-success ms-1 me-1">Present (P)</span>
              <span className="badge bg-danger ms-1 me-1">Absent (A)</span>
              <span className="badge bg-warning ms-1 me-1">Excused (E)</span>
              <span className="badge bg-primary ms-1 me-1">Self-paced (SP)</span>
              <span className="badge bg-secondary ms-1 me-1">Other (O)</span>
              <span className="badge bg-info ms-1">Incomplete</span>
            </small>
          </div>
          <Button
            variant="primary"
            onClick={() => setShowConfirmModal(true)}
            disabled={getOverrideCount() === 0 || loading}
          >
            Submit {getOverrideCount()} Overrides
          </Button>
        </div>
      </Card.Body>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Attendance Overrides</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <h6>⚠️ You are about to override attendance for {getOverrideCount()} learners</h6>
            <p>This action will:</p>
            <ul>
              <li>Update attendance records in the system</li>
              <li>Override any existing Teams attendance data</li>
              <li>Create an audit trail for POPIA compliance</li>
              <li>Send notifications to flagged learners (if enabled)</li>
            </ul>
          </Alert>

          <div className="mb-3">
            <h6>Override Summary:</h6>
            <Table size="sm" striped>
              <thead>
                <tr>
                  <th>Learner</th>
                  <th>New Status</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(overrides).map(([learnerId, override]) => {
                  const learner = learners.find(l => l.id === learnerId);
                  return (
                    <tr key={learnerId}>
                      <td>{learner?.name}</td>
                      <td>{getStatusBadge(override.status)}</td>
                      <td><small>{override.reason || 'No reason specified'}</small></td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          <Form.Group>
            <Form.Label>Confirm with your password (optional):</Form.Label>
            <Form.Control
              type="password"
              placeholder="Enter your password to confirm"
            />
            <Form.Text className="text-muted">
              For security and audit purposes
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitOverrides}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Submitting...
              </>
            ) : (
              'Confirm Overrides'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default AttendanceOverride;
